import './i18n';
import './cineforge/i18n';
import './cineforge/fontScaleMigrate'; // CineForge : reporte l'ancien réglage (cineforge.fontScale) avant lib/fontScale
import './lib/fontScale'; // "Text size" setting, applied before the first render
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AppDialogHost } from "./lib/appDialog";
import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import axios from "axios";
import ErrorBoundary from "./components/ErrorBoundary";
import DocumentTitle from "./components/DocumentTitle";
import useVersionCheck from "./hooks/useVersionCheck";
import { StaleBuildContext } from "./components/VersionNotice";
import { takeLogoutUrl, isSsoSession, forgetSession, secondsLeft, loginPathFor, clearAutoSso, markLoggingOut, isLoggingOut, allowAutoSsoAfterSessionEnd, isSsoSessionEnded } from "./lib/oidc";
import { can } from "./lib/roles";
import { installSoundHooks } from "./lib/sounds";
import { installPasteToDrop } from "./lib/pasteToDrop";
import TaskEndWatcher from "./components/TaskEndWatcher";
import ConsentRoute from "./components/ConsentRoute";
import ActiveJobsPoller from "./components/ActiveJobsDock";
import { clearToastLog } from "./lib/toastLog";
import { useAccountTours } from "./components/OnboardingTour";

// barre latérale visible (lg, ≥ 1024 px) : les popups se posent à sa droite ; en dessous elle est repliée → bord gauche
function useSidebarShown() {
  const query = "(min-width: 1024px)";
  const [shown, setShown] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setShown(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return shown;
}

// Sons de notification : erreurs (toast.error) + plan de l'agent terminé — idempotent (PLAN_SONS_NOTIFICATION.md)
installSoundHooks();

// Retry wrapper — auto-reload on ChunkLoadError (stale cache after deploy)
const lazyWithRetry = (importFn) =>
  lazy(() =>
    importFn().catch((error) => {
      // webpack : ChunkLoadError / « Loading chunk » ; Vite : « Failed to fetch dynamically imported module »
      // (Chrome), « error loading dynamically imported module » (Firefox), « Importing a module script failed » (Safari),
      // « Unable to preload CSS » (CSS d'une page périmé après un déploiement, avant même l'import du module)
      if (error.name === "ChunkLoadError" || /Loading chunk|dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(error.message || "")) {
        const reloaded = sessionStorage.getItem("chunk_reload");
        if (!reloaded) {
          sessionStorage.setItem("chunk_reload", "1");
          window.location.reload();
          return new Promise(() => {});
        }
        sessionStorage.removeItem("chunk_reload");
      }
      throw error;
    })
  );

// Pages — lazy loaded for code splitting
const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const ProjectCreatePage = lazyWithRetry(() => import("./pages/ProjectCreatePage"));
const ProjectDetailPage = lazyWithRetry(() => import("./pages/ProjectDetailPage"));
const BriefAnalysisPage = lazyWithRetry(() => import("./pages/BriefAnalysisPage"));
const BreakdownPage = lazyWithRetry(() => import("./pages/BreakdownPage"));
const ProductionPage = lazyWithRetry(() => import("./pages/ProductionPage"));
const DirectorVisionPage = lazyWithRetry(() => import("./pages/DirectorVisionPage"));
const TimelinePage = lazyWithRetry(() => import("./pages/TimelinePage"));
const ReportPage = lazyWithRetry(() => import("./pages/ReportPage"));
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const RnDPage = lazyWithRetry(() => import("./pages/RnDPage"));
const ControlRoomPage = lazyWithRetry(() => import("./pages/ControlRoomPage"));
const LibraryPage = lazyWithRetry(() => import("./pages/LibraryPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const CommentsPage = lazyWithRetry(() => import("./pages/CommentsPage"));
const ToolboxPage = lazyWithRetry(() => import("./pages/ToolboxPage"));
const SeriesPage = lazyWithRetry(() => import("./pages/SeriesPage"));
const ScenarioV2Page = lazyWithRetry(() => import("./pages/ScenarioV2Page"));
const ProductionPlateauPage = lazyWithRetry(() => import("./pages/ProductionPlateauPage"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const OidcCallback = lazyWithRetry(() => import("./pages/OidcCallback"));
const OAuthConsentPage = lazyWithRetry(() => import("./pages/OAuthConsentPage"));
const AgentDrawer = lazyWithRetry(() => import("./components/agent/AgentDrawer"));   // lazy : casse le cycle App ↔ useAuth

/** Un échec du tiroir (chunk, rendu) ne doit jamais démonter l'application : boundary à rendu nul, HORS du chunk. */
class AgentBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error("[AgentDrawer] masqué après une erreur :", err); }
  render() { return this.state.failed ? null : this.props.children; }
}

/** Le chunk n'est téléchargé que si l'agent est activé sur cette instance (prod : jamais). */
function AgentMount() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [enabled, setEnabled] = useState(false);
  const onConsent = pathname.startsWith("/oauth/");   // page de consentement Claude : ni tiroir ni appel de l'agent
  useEffect(() => {
    if (!user || onConsent) { setEnabled(false); return; }
    let cancelled = false;
    axios.get(`${API}/agent/enabled`).then((r) => { if (!cancelled) setEnabled(!!r.data?.enabled); }).catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, [user, onConsent]);
  if (!enabled || onConsent) return null;
  return <AgentBoundary><Suspense fallback={null}><AgentDrawer /></Suspense></AgentBoundary>;
}

// CineForge
const PromptsAdminPage = lazyWithRetry(() => import("./cineforge/prompts/PromptsAdminPage"));
const ProjectPromptsPage = lazyWithRetry(() => import("./cineforge/prompts/ProjectPromptsPage"));

/** Sons de fin de tâche même page fermée (PLAN_SONS_NOTIFICATION.md, Lot 1-bis) — connecté seulement. */
function TaskEndMount() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // page de consentement Claude : aucune requête annexe tant que ?req= peut encore être dans l'URL (Referer)
  return user?.id && !pathname.startsWith("/oauth/") ? <TaskEndWatcher api={API} userId={user.id} /> : null;
}

/** Tâches en cours (cf-1vy) : le poller, une fois ici (survit aux pages) ; les boutons sont dans AppLayout / AppSidebar. */
function ActiveJobsMount() {
  const { user } = useAuth();
  return user?.id ? <ActiveJobsPoller api={API} userId={user.id} user={user} /> : null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Session Keycloak (SSO) : JWT Studio de 30 min, rafraîchi contre Keycloak par le backend. Une
// seule requête de rafraîchissement à la fois dans l'onglet (le backend sérialise entre onglets).
let ssoRefreshInFlight = null;
const refreshSsoToken = () => {
  if (!ssoRefreshInFlight) {
    const current = localStorage.getItem("token");
    ssoRefreshInFlight = axios
      .post(`${API}/auth/oidc/refresh`, null, { headers: { Authorization: `Bearer ${current}` } })
      .then((res) => {
        const fresh = res.data.access_token;
        localStorage.setItem("token", fresh);
        axios.defaults.headers.common["Authorization"] = `Bearer ${fresh}`;
        // l'état React suit (minuterie, consommateurs de useAuth().token)
        window.dispatchEvent(new CustomEvent("studio-token-refreshed", { detail: fresh }));
        return fresh;
      })
      .finally(() => { ssoRefreshInFlight = null; });
  }
  return ssoRefreshInFlight;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const toursReady = useAccountTours(user);   // cf-i8vh : visite guidée vue une fois par compte

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        try {
          let res;
          try {
            res = await axios.get(`${API}/auth/me`);
          } catch (e) {
            // session SSO expirée : un rafraîchissement silencieux avant de renvoyer à la connexion
            if (e.response?.status !== 401 || !isSsoSession()) throw e;
            try {
              setToken(await refreshSsoToken());
            } catch (re) {
              if (re.response?.status !== 401) {   // Keycloak injoignable : on garde la session
                setLoading(false);
                return;
              }
              // même règle au chargement de la page (retour du hub dans le même onglet) que dans l'intercepteur
              if (isSsoSessionEnded(re)) allowAutoSsoAfterSessionEnd();
              throw re;
            }
            res = await axios.get(`${API}/auth/me`);
          }
          setUser(res.data);
        } catch (e) {
          // Token invalid, clear it
          localStorage.removeItem("token");
          delete axios.defaults.headers.common["Authorization"];
          forgetSession();
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []); // Run only once on mount

  // cf-aj0 : la réponse de connexion n'a pas les droits par section — /auth/me les donne
  useEffect(() => {
    if (!user || user.capabilities) return;
    axios.get(`${API}/auth/me`).then((res) => setUser((u) => (u && u.id === res.data.id ? { ...u, ...res.data } : u)))
      .catch(() => {});
  }, [user]);

  // cf-aj0 : un refus de rôle sur une ACTION se dit clairement ; une lecture refusée (données d'une section cachée
  // qu'une page charge au passage) reste silencieuse
  useEffect(() => {
    const interceptor = axios.interceptors.response.use((r) => r, (error) => {
      const detail = error.response?.data?.detail;
      const method = (error.config?.method || "get").toLowerCase();
      if (error.response?.status === 403 && detail?.code === "role_denied" && method !== "get") {
        toast.error(detail.message, { id: `role-denied-${detail.need}` });
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Auto-logout on 401 response (except auth endpoints)
  useEffect(() => {
    let redirecting = false;
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const cfg = error.config || {};
        // déconnexion Keycloak en route : un 401 d'une requête en vol ne doit pas remplacer cette navigation
        if (isLoggingOut()) return Promise.reject(error);
        if (error.response?.status === 401 && !cfg.url?.includes('/auth/') && !redirecting) {
          // session SSO : un seul essai de rafraîchissement, puis la requête est rejouée
          if (isSsoSession() && !cfg._ssoRetried) {
            try {
              const fresh = await refreshSsoToken();
              cfg._ssoRetried = true;
              cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${fresh}` };
              return axios(cfg);
            } catch (re) {
              // seul un 401 dit « session terminée » ; 502/429 : Keycloak lent, la session reste
              if (re.response?.status !== 401) return Promise.reject(error);
              // Keycloak a fermé la session (déconnexion, changement de rôle) : pas un rebond d'API, /login repart
              // vers la connexion centrale sans clic (une fois par minute au plus, cf. oidc.js)
              if (isSsoSessionEnded(re)) allowAutoSsoAfterSessionEnd();
            }
          }
          redirecting = true;
          localStorage.removeItem("token");
          delete axios.defaults.headers.common["Authorization"];
          forgetSession();
          // NE PAS appeler clearAutoSso() ici : la marque « succès récent » est ce qui arrête un
          // rebond Studio <-> Keycloak quand une API renvoie 401 en boucle
          window.location.href = loginPathFor(window.location);   // retour au lien d'origine après connexion
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Rafraîchissement fait ailleurs (intercepteur) : l'état React suit
  useEffect(() => {
    const onRefreshed = (e) => setToken(e.detail);
    window.addEventListener("studio-token-refreshed", onRefreshed);
    return () => window.removeEventListener("studio-token-refreshed", onRefreshed);
  }, []);

  // Session SSO : rafraîchir 5 min avant l'expiration du JWT Studio (30 min)
  useEffect(() => {
    if (!token || !isSsoSession()) return undefined;
    const left = secondsLeft(token);
    if (left === null) return undefined;
    const timer = setTimeout(() => {
      refreshSsoToken().then(setToken).catch(() => {});   // échec : le prochain 401 renvoie à la connexion
    }, Math.max(60, left - 300) * 1000);      // au moins une minute entre deux rafraîchissements
    return () => clearTimeout(timer);
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    forgetSession();                       // connexion par mot de passe : pas de session SSO
    localStorage.setItem("token", res.data.access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    if (res.data.status === "pending") {
      return res.data; // No token — account pending approval
    }
    localStorage.setItem("token", res.data.access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };
  
  // Handle Google OAuth login success
  const handleGoogleLogin = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
  };

  const logout = () => {
    // le serveur oublie la session SSO (refresh token Keycloak chiffré) ; keepalive : survit à la
    // redirection vers Keycloak
    const current = localStorage.getItem("token");
    if (current) {
      try {
        // session SSO : route qui accepte aussi un JWT expiré (déconnexion après inactivité)
        const path = isSsoSession() ? "/auth/oidc/logout" : "/auth/logout";
        fetch(`${API}${path}`, { method: "POST", keepalive: true, headers: { Authorization: `Bearer ${current}` } }).catch(() => {});
      } catch {
        // déconnexion locale quand même
      }
    }
    // connexion Keycloak : terminer aussi la session SSO, sinon « déconnexion » reconnecte en silence. Marqué
    // AVANT les mises à jour d'état : /login, rendu avant le départ, ne doit pas relancer une connexion
    const ssoLogout = takeLogoutUrl();
    if (ssoLogout) markLoggingOut();
    localStorage.removeItem("token");
    clearToastLog();    // les messages récents d'un compte ne restent pas pour le suivant (navigateur partagé)
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    clearAutoSso();     // déconnexion voulue : la visite suivante va droit à la connexion centrale
    if (ssoLogout) window.location.href = ssoLogout;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, toursReady, login, register, logout, handleGoogleLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, section, need = "view" }) => {
  const { user, loading, toursReady } = useAuth();
  const location = useLocation();
  
  if (loading || !toursReady) {   // cf-i8vh : visites guidées alignées sur le compte avant que la page ne décide
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to={loginPathFor(location)} replace />;
  // cf-aj0 : section cachée pour ce rôle -> tableau de bord
  if (section && !can(user, section, need)) return <Navigate to="/" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!user) return <Navigate to={loginPathFor(location)} replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

// Wrapper component that handles OAuth callback detection
function AppRouter() {
  const { handleGoogleLogin, user, loading } = useAuth();
  
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#141416] flex items-center justify-center"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google/callback" element={<AuthCallback onLoginSuccess={handleGoogleLogin} />} />
      <Route path="/auth/callback" element={<OidcCallback onLoginSuccess={handleGoogleLogin} />} />
      <Route path="/oauth/consent" element={<ConsentRoute user={user} loading={loading}><OAuthConsentPage /></ConsentRoute>} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/projects/new" element={<ProtectedRoute section="dashboard" need="edit"><ProjectCreatePage /></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
      <Route path="/projects/:id/series" element={<ProtectedRoute><SeriesPage /></ProtectedRoute>} />
      <Route path="/projects/:id/scenario-v2" element={<ProtectedRoute section="scenario"><ScenarioV2Page /></ProtectedRoute>} />
      <Route path="/projects/:id/analysis" element={<ProtectedRoute section="brief"><BriefAnalysisPage /></ProtectedRoute>} />
      <Route path="/projects/:id/vision" element={<ProtectedRoute section="vision"><DirectorVisionPage /></ProtectedRoute>} />
      <Route path="/projects/:id/breakdown" element={<ProtectedRoute section="breakdown"><BreakdownPage /></ProtectedRoute>} />
      <Route path="/projects/:id/shots" element={<ProtectedRoute section="breakdown"><BreakdownPage /></ProtectedRoute>} />
      <Route path="/projects/:id/production" element={<ProtectedRoute section="production"><ProductionPage /></ProtectedRoute>} />
      <Route path="/projects/:id/plateau" element={<ProtectedRoute section="production"><ProductionPlateauPage /></ProtectedRoute>} />
      <Route path="/projects/:id/timeline" element={<ProtectedRoute section="timeline"><TimelinePage /></ProtectedRoute>} />
      <Route path="/projects/:id/toolbox" element={<ProtectedRoute section="toolbox"><ToolboxPage /></ProtectedRoute>} />
      <Route path="/projects/:id/report" element={<ProtectedRoute section="report"><ReportPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/comments" element={<ProtectedRoute section="comments"><CommentsPage /></ProtectedRoute>} />
      <Route path="/toolbox" element={<ProtectedRoute section="toolbox"><ToolboxPage /></ProtectedRoute>} />
      <Route path="/bible" element={<Navigate to="/toolbox?section=bible" replace />} />
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/rnd" element={<AdminRoute><RnDPage /></AdminRoute>} />
      <Route path="/admin/control-room" element={<AdminRoute><ControlRoomPage /></AdminRoute>} />
      <Route path="/admin/prompts" element={<AdminRoute><PromptsAdminPage /></AdminRoute>} />
      <Route path="/projects/:id/prompts" element={<AdminRoute><ProjectPromptsPage /></AdminRoute>} />
      {/* Bibliotheque ouverte a tous les roles : /library/media est scope cote serveur
          (un membre ne recoit que ses projets + ses assets toolbox, realisateur/admin voient tout). */}
      <Route path="/library" element={<ProtectedRoute section="library"><LibraryPage /></ProtectedRoute>} />
    </Routes>
    </Suspense>
  );
}

function App() {
  // Détecte un nouveau déploiement : avis permanent « Recharger » (barre latérale + en-tête mobile), jamais de rechargement forcé.
  const staleBuild = useVersionCheck();
  const sidebarShown = useSidebarShown();

  // Click-to-dismiss toasts
  useEffect(() => {
    const handler = (e) => {
      const toastEl = e.target.closest('[data-sonner-toast]');
      if (toastEl) {
        const id = toastEl.getAttribute('data-sonner-toast');
        if (id) toast.dismiss(id);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // ⌘V d'une image = dépôt sur la zone survolée (toutes les zones de dépôt de l'app)
  useEffect(() => installPasteToDrop(), []);

  return (
    <StaleBuildContext.Provider value={staleBuild}>
    <AuthProvider>
      <BrowserRouter>
        <AppDialogHost />
        {/* Alex 2026-10-28 : « refaire la UX des popups, en bas à gauche, là où la file et le log s'affichent » — à
            côté du bas de la barre latérale (w-60 = 240 px), dépliées l'une sous l'autre (jamais empilées), et gardées
            dans « Messages récents » de la cloche (lib/toastLog) */}
        <Toaster
          position="bottom-left"
          offset={{ left: sidebarShown ? 256 : 16, bottom: 24 }}
          mobileOffset={{ left: 16, bottom: 16 }}
          expand
          visibleToasts={5}
          style={{ zIndex: 99999 }}
          // Alex 2026-10-26 : un message d'erreur disparaissait avant d'être lu — plus long, et une croix pour fermer
          duration={10000}
          closeButton
          theme="dark"
          toastOptions={{
            style: {
              background: '#1A1A2E',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)',
              cursor: 'pointer'
            }
          }}
        />
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
        {/* Agent de production : monté ici (sous BrowserRouter, hors Routes) pour survivre aux navigations —
            un tour en vol ou une exécution en cours ne sont pas perdus. Caché si l'agent n'est pas activé. */}
        <AgentMount />
        <DocumentTitle />
        <TaskEndMount />
        <ActiveJobsMount />
      </BrowserRouter>
    </AuthProvider>
    </StaleBuildContext.Provider>
  );
}

export default App;
